/**
 * Turn a stored WhatsApp message into something the chat can render.
 *
 * Some messages store a JSON payload as a string in `message_text` (interactive
 * button/list/link messages, and outgoing template and media sends). Parse it
 * into a normalized shape, or return null when the text is plain content.
 *
 * Kept free of UI and API imports so it can be unit tested on its own.
 */

const INTERACTIVE_TYPES = new Set([
  'interactive_reply_buttons',
  'interactive_button',
  'interactive_list',
  'interactive_list_messages',
  'interactive_cta_url',
  'project_list',
  'interactive'
]);

// Stored list payloads keep their choices in `sections[].rows` (the booking
// picker, project lists), so these are lists even without a top-level `rows`.
const LIST_TYPES = new Set(['interactive_list', 'interactive_list_messages', 'project_list']);

const MEDIA_TYPES = new Set(['document', 'image', 'video', 'audio']);

function humanizeTemplateName(name) {
  const cleaned = String(name || '')
    .replace(/_/g, ' ')
    .replace(/\bv\d+\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return 'Template message';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function parseRichMessage(message) {
  const type = message?.message_type;
  const raw = message?.message_text;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;

  let payload;
  try {
    payload = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!payload || typeof payload !== 'object') return null;

  // Template send payloads (messageType: "template").
  if (type === 'template' || payload.messageType === 'template' || payload.templateName) {
    const name = humanizeTemplateName(payload.templateName);
    const collectValues = (obj) =>
      obj && typeof obj === 'object'
        ? Object.values(obj)
            .map((value) => String(value ?? '').trim())
            .filter(Boolean)
        : [];
    const variables = [
      ...collectValues(payload.headerVariables),
      ...collectValues(payload.bodyVariables)
    ];
    const fileUrl = String(payload.templateFileUrl ?? '').trim();
    const mediaUrl = /^https?:\/\/.+\.(png|jpe?g|gif|webp)(\?.*)?$/i.test(fileUrl) ? fileUrl : '';
    return { kind: 'template', name, variables, mediaUrl };
  }

  // Media send payloads (messageType: "document" | "image" | "video" | "audio").
  if (MEDIA_TYPES.has(type) || MEDIA_TYPES.has(payload.messageType) || MEDIA_TYPES.has(payload.type)) {
    const mediaType = MEDIA_TYPES.has(payload.messageType)
      ? payload.messageType
      : MEDIA_TYPES.has(payload.type)
      ? payload.type
      : type;
    // Some payloads only carry { type, filename, caption } and keep the real
    // media URL in the metadata rather than in message_text. Prefer our own
    // permanent S3 copy over the pinggo `lookaside.fbsbx.com` link, which is a
    // short-lived Meta URL (its `ext` query param is an expiry timestamp).
    const s3Url = String(message?.metadata?.media?.s3Url ?? '').trim();
    const pinggoMsg =
      message?.metadata?.pinggo?.data?.messages?.[0] ?? message?.metadata?.data?.messages?.[0];
    const metaUrl =
      pinggoMsg?.documentUrl ||
      pinggoMsg?.imageUrl ||
      pinggoMsg?.videoUrl ||
      pinggoMsg?.audioUrl ||
      '';
    const url = String(
      payload.sourceUrl ?? payload.mediaUrl ?? payload.url ?? s3Url ?? metaUrl ?? ''
    ).trim();
    const caption = String(payload.messageText ?? payload.caption ?? payload.text ?? '').trim();
    const filename = String(
      payload.filename ?? payload.fileName ?? message?.metadata?.media?.filename ?? ''
    ).trim();
    return { kind: 'media', mediaType, url, caption, filename };
  }

  const header = String(payload.headerText ?? payload.header ?? '').trim();
  const body = String(payload.bodyText ?? payload.body ?? payload.text ?? '').trim();
  const footer = String(payload.footerText ?? payload.footer ?? '').trim();

  const normalizeButton = (item) => {
    if (item == null) return null;
    if (typeof item === 'string') {
      const label = item.trim();
      return label ? { label } : null;
    }
    if (typeof item === 'object') {
      const label = String(item.title ?? item.label ?? item.text ?? '').trim();
      const description = String(item.description ?? '').trim();
      if (!label) return null;
      return description ? { label, description } : { label };
    }
    return null;
  };

  // List choices live in `rows`, or in `sections[].rows` for WhatsApp list
  // messages such as the booking date/time picker.
  const sectionRows = Array.isArray(payload.sections)
    ? payload.sections.flatMap((section) => (Array.isArray(section?.rows) ? section.rows : []))
    : [];
  const listRows = [...(Array.isArray(payload.rows) ? payload.rows : []), ...sectionRows];
  const isList = LIST_TYPES.has(type) || listRows.length > 0;
  const options = isList ? listRows.map(normalizeButton).filter(Boolean) : [];

  const buttonText = String(payload.buttonText ?? '').trim();
  const buttonUrl = String(payload.buttonUrl ?? payload.url ?? '').trim();
  const isLink = type === 'interactive_cta_url' || Boolean(buttonUrl);

  let buttons;
  if (isList) {
    // WhatsApp shows a list as one button that opens the choices.
    buttons = [{ label: buttonText || 'View options' }];
  } else if (isLink) {
    buttons = buttonText || buttonUrl ? [{ label: buttonText || 'Open link', url: buttonUrl }] : [];
  } else {
    const rawButtons = Array.isArray(payload.buttons)
      ? payload.buttons
      : Array.isArray(payload.options)
      ? payload.options
      : [];
    buttons = rawButtons.map(normalizeButton).filter(Boolean);
  }

  // Only treat it as interactive if we actually recognized the shape.
  if (!buttons.length && !options.length && !INTERACTIVE_TYPES.has(type)) return null;

  return { kind: 'interactive', header, body, footer, buttons, options, isList, isLink };
}
